import { faEllipsis, faEnvelope, faEnvelopeOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Avatar from '../../../components/base/Avatar';
import Button from '../../../components/base/Button';
import IndeterminateCheckbox from '../../../components/base/IndeterminateCheckbox';
import StarCheckbox from '../../../components/base/StarCheckbox';
import { Email } from '../../../data/email';
import { getFileIcon } from '../../../helpers/utils';
import { useBulkSelect } from '../../../providers/BulkSelectProvider';
import { Col, Dropdown, Row, Spinner } from 'react-bootstrap';
import { useEmailRead } from '../../../hooks/useEmailRead';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

interface EmailRowProps {
  email: Email;
  index: number;
  folderPath: string;
}

// 🔧 FUNCIÓN MEJORADA PARA DECODIFICAR CARACTERES MAL CODIFICADOS
const fixTextEncoding = (text: string): string => {
  if (!text) return '';
  
  try {
    // Primero intentar detectar si ya está bien codificado
    if (!/[ÃÂ]/.test(text) && /[áéíóúñü]/i.test(text)) {
      return text;
    }
    
    // Mapeo completo de caracteres UTF-8 malinterpretados
    const encodingMap: { [key: string]: string } = {
      // Vocales con tildes
      'Ã¡': 'á', 'Ã©': 'é', 'Ã­': 'í', 'Ã³': 'ó', 'Ãº': 'ú',
      'Ã': 'Á', 'Ã‰': 'É', 'Ã': 'Í', 'Ã“': 'Ó', 'Ãš': 'Ú',
      
      // Eñe
      'Ã±': 'ñ', 'Ã\'': 'Ñ',
      
      // Diéresis
      'Ã¤': 'ä', 'Ã«': 'ë', 'Ã¯': 'ï', 'Ã¶': 'ö', 'Ã¼': 'ü',
      'Ã„': 'Ä', 'Ã‹': 'Ë', 'Ã': 'Ï', 'Ã–': 'Ö', 'Ãœ': 'Ü',
      
      // Graves
      'Ã ': 'à', 'Ã¨': 'è', 'Ã¬': 'ì', 'Ã²': 'ò', 'Ã¹': 'ù',
      'Ã€': 'À', 'Ãˆ': 'È', 'ÃŒ': 'Ì', 'Ã’': 'Ò', 'Ã™': 'Ù',
      
      // Circunflejos
      'Ã¢': 'â', 'Ãª': 'ê', 'Ã®': 'î', 'Ã´': 'ô', 'Ã»': 'û',
      'Ã‚': 'Â', 'ÃŠ': 'Ê', 'ÃŽ': 'Î', 'Ã"': 'Ô', 'Ã›': 'Û',
      
      // Otros diacríticos
      'Ã§': 'ç', 'Ã‡': 'Ç',
      'Ã¸': 'ø', 'Ã˜': 'Ø',
      'Ã¥': 'å', 'Ã…': 'Å',
      'Ã¦': 'æ', 'Ã†': 'Æ',
      'Ã°': 'ð', 'Ã': 'Ð',
      'Ã¾': 'þ', 'Ãž': 'Þ',
      'ÃŸ': 'ß',
      
      // Puntuación
      'â€™': "'", 'â€˜': "'",
      'â€œ': '"', 'â€': '"',
      'â€“': '–', 'â€”': '—',
      'â€¦': '…',
      'â‚¬': '€',
      'â„¢': '™',
      
      // Símbolos
      'Â©': '©', 'Â®': '®',
      'Â°': '°', 'Â±': '±',
      'Â¿': '¿', 'Â¡': '¡',
      'Â§': '§', 'Â¶': '¶',
      'Â«': '«', 'Â»': '»',
      'Â·': '·', 'Âµ': 'µ',
      
      // Espacios especiales
      'Â ': ' ', 'â€¯': ' ', 'â€ƒ': ' ', 'â€‚': ' ',
      'â€„': ' ', 'â€…': ' ', 'â€†': ' ', 'â€‡': ' ',
      'â€ˆ': ' ', 'â€‰': ' ', 'â€Š': ' ',
      
      // Caracteres especiales
      'â€‹': '', 'ï»¿': '',
      'âœ"': '✓', 'âœ—': '✗',
      'â†’': '→', 'â†←': '←', 'â†↑': '↑', 'â†↓': '↓',
      
      // Matemáticos
      'Ã—': '×', 'Ã·': '÷', 'â‰ ': '≠',
      'â‰¤': '≤', 'â‰¥': '≥', 'â‰ˆ': '≈',
      'âˆž': '∞', 'âˆ‘': '∑', 'âˆ†': '∆',
      'âˆš': '√', 'âˆ«': '∫', 'âˆ‚': '∂'
    };
    
    let fixed = text;
    
    // Aplicar correcciones (más largos primero para evitar conflictos)
    Object.entries(encodingMap)
      .sort(([a], [b]) => b.length - a.length)
      .forEach(([encoded, decoded]) => {
        if (fixed.includes(encoded)) {
          const escaped = encoded.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          fixed = fixed.replace(new RegExp(escaped, 'g'), decoded);
        }
      });
    
    // Entidades HTML
    const htmlEntities: { [key: string]: string } = {
      '&amp;': '&', '&lt;': '<', '&gt;': '>',
      '&quot;': '"', '&apos;': "'", '&#39;': "'",
      '&nbsp;': ' ', '&mdash;': '—', '&ndash;': '–',
      '&ldquo;': '"', '&rdquo;': '"',
      '&lsquo;': "'", '&rsquo;': "'",
      '&hellip;': '…', '&copy;': '©', '&reg;': '®'
    };
    
    Object.entries(htmlEntities).forEach(([entity, char]) => {
      fixed = fixed.replace(new RegExp(entity, 'g'), char);
    });
    
    // Entidades numéricas
    fixed = fixed.replace(/&#(\d+);/g, (match, dec) => {
      try {
        const code = parseInt(dec, 10);
        return (code > 0 && code < 1114112) ? String.fromCharCode(code) : match;
      } catch { return match; }
    });
    
    fixed = fixed.replace(/&#x([0-9A-F]+);/gi, (match, hex) => {
      try {
        const code = parseInt(hex, 16);
        return (code > 0 && code < 1114112) ? String.fromCharCode(code) : match;
      } catch { return match; }
    });
    
    // Normalizar espacios
    return fixed
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\u00A0/g, ' ');
    
  } catch (error) {
    console.warn('Error fixing encoding:', error);
    return text;
  }
};

// 🔧 FUNCIÓN PARA LIMPIAR CONTENIDO DEL EMAIL
const cleanEmailContent = (content: string): string => {
  if (!content) return '';
  
  let cleaned = fixTextEncoding(content);
  
  // Remover headers MIME
  if (cleaned.includes('Content-Type:') || cleaned.includes('Content-Transfer-Encoding:')) {
    cleaned = cleaned.replace(/Content-Type:\s*[^\r\n]*/gi, '');
    cleaned = cleaned.replace(/Content-Transfer-Encoding:\s*[^\r\n]*/gi, '');
    cleaned = cleaned.replace(/Content-Disposition:\s*[^\r\n]*/gi, '');
    cleaned = cleaned.replace(/MIME-Version:\s*[^\r\n]*/gi, '');
    cleaned = cleaned.replace(/charset\s*=\s*[^\s;,\r\n]*/gi, '');
    cleaned = cleaned.replace(/--[a-zA-Z0-9_-]{8,}/g, '');
    cleaned = cleaned.replace(/--=_[a-zA-Z0-9_-]+/g, '');
    cleaned = cleaned.replace(/^[A-Za-z-]+:\s*[^\r\n]*$/gm, '');
  }
  
  // Remover respuestas
  cleaned = cleaned.replace(/^(Re:|RE:|Fw:|FW:|Fwd:|FWD:)\s*/i, '');
  cleaned = cleaned.replace(/El .+?(escribió|escribiÃ³|wrote):\s*>?/gi, '');
  cleaned = cleaned.replace(/On .+?(wrote|escribió):\s*>?/gi, '');
  cleaned = cleaned.replace(/^>\s*.*/gm, '');
  cleaned = cleaned.replace(/^(From|De|Sent|Enviado|To|Para|Subject|Asunto|Date|Fecha):\s*.*/gmi, '');
  
  // Remover firmas
  cleaned = cleaned.replace(/^--\s*$/gm, '');
  cleaned = cleaned.replace(/Sent from .+$/gmi, '');
  cleaned = cleaned.replace(/Enviado desde .+$/gmi, '');
  cleaned = cleaned.replace(/Get Outlook for .+$/gmi, '');
  
  // Limpiar espacios
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
  cleaned = cleaned.replace(/\r?\n\r?\n\r?\n/g, '\n\n');
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  cleaned = cleaned.replace(/[\u200B-\u200D\uFEFF]/g, '');
  
  return cleaned;
};

const truncateText = (text: string, maxLength: number = 150): string => {
  if (!text || text.length <= maxLength) return text;
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > maxLength * 0.7 ? truncated.substring(0, lastSpace) : truncated) + '...';
};

const cleanSubject = (subject: string): string => {
  if (!subject) return '';
  let cleaned = fixTextEncoding(subject);
  cleaned = cleaned.replace(/^(Re:\s*)+(Re:\s*)?/i, 'Re: ');
  cleaned = cleaned.replace(/^(Fw:\s*)+(Fw:\s*)?/i, 'Fw: ');
  cleaned = cleaned.replace(/^(Fwd:\s*)+(Fwd:\s*)?/i, 'Fwd: ');
  return cleaned.trim();
};

const EmailRow = ({ email, index, folderPath }: EmailRowProps) => {
  const { getRowCheckboxProps } = useBulkSelect();
  const { toggleReadStatus, loading } = useEmailRead();
  const [localIsRead, setLocalIsRead] = useState<boolean | null>(null);
  const navigate = useNavigate();
  
  // Estado de lectura
  const isEmailRead = (): boolean => {
    if (localIsRead !== null) return localIsRead;
    if (email.read_at) return true;
    
    const extendedEmail = email as any;
    if (typeof extendedEmail.isRead === 'boolean') return extendedEmail.isRead;
    if (typeof extendedEmail.isNew === 'boolean') return !extendedEmail.isNew;
    if (extendedEmail.flags?.includes('\\Seen')) return true;
    
    return false;
  };
  
  const isRead = isEmailRead();

  // 🔧 FUNCIÓN: Navegar al email (click principal)
  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Solo navegar si no hay elementos interceptando
    if (e.defaultPrevented) return;
    
    console.log(`📧 Opening email ${email.id} from folder ${folderPath}`);
    
    // Navegar al detalle del email
    navigate('/anubis/email/email-detail', {
      state: { 
        emailId: email.id, 
        folderPath: folderPath 
      }
    });
  };

  // 🔧 FUNCIÓN: Cambiar estado de lectura (botón específico)
  const handleToggleReadStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Evitar que se propague al click principal
    
    try {
      setLocalIsRead(!isRead);
      const result = await toggleReadStatus(folderPath, Number(email.id), isRead);
      
      if (!result.success) {
        setLocalIsRead(isRead);
        alert(`❌ ${result.error}`);
      } else {
        console.log(`✅ ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Error:', error);
      setLocalIsRead(isRead);
      alert('❌ Error al cambiar el estado de lectura');
    }
  };
  
  // Aplicar limpiezas
  const cleanedSender = fixTextEncoding(email.sender.name);
  const cleanedSubject = cleanSubject(email.subject);
  const cleanedDetails = truncateText(cleanEmailContent(email.details));
  
  return (
    <div 
      className={classNames(
        "border-bottom border-translucent hover-actions-trigger py-3 email-row",
        {
          'email-read': isRead,
          'email-unread': !isRead,
          'email-updating': loading
        }
      )}
      onClick={handleEmailClick} // ← CLICK PRINCIPAL PARA ABRIR EMAIL
      style={{ cursor: 'pointer' }}
    >
      <Row className="gx-2">
        <Col xs="auto">
          <div className="d-flex flex-column flex-sm-row">
            <IndeterminateCheckbox
              {...getRowCheckboxProps(String(index))}
              className="mb-2 m-sm-0 me-sm-2"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
            <Button 
              className="btn p-0"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <StarCheckbox defaultChecked={email.starred} iconClass="fs-9" />
            </Button>
          </div>
        </Col>
        
        {/* 📧 BOTÓN ESPECÍFICO PARA CAMBIAR ESTADO DE LECTURA */}
        <Col className="col-auto" style={{ width: '24px' }}>
          <Button
            variant="link"
            size="sm"
            className="p-1 text-body-tertiary"
            onClick={handleToggleReadStatus} // ← FUNCIÓN ESPECÍFICA
            title={isRead ? 'Marcar como no leído' : 'Marcar como leído'}
            disabled={loading}
          >
            <FontAwesomeIcon 
              icon={isRead ? faEnvelopeOpen : faEnvelope} 
              className="fs-9"
              style={{ color: isRead ? '#6c757d' : '#007bff' }}
            />
          </Button>
        </Col>
        
        <Col className="col-auto">
          {email.sender.avatar ? (
            <Avatar src={email.sender.avatar} size="s" />
          ) : (
            <Avatar size="s" variant="name">
              {cleanedSender.charAt(0).toUpperCase()}
            </Avatar>
          )}
        </Col>
        
        <Col className="col-auto">
          <span
            className={classNames('fs-9', {
              'text-body fw-normal': isRead,
              'text-body-emphasis fw-bold': !isRead
            })}
            title={cleanedSender}
          >
            {cleanedSender}
          </span>
        </Col>
        
        <Col className="col-auto ms-auto">
          <div className="hover-actions end-0">
            <Dropdown onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <Dropdown.Toggle
                variant="phoenix-secondary"
                className="dropdown-caret-none btn-icon"
              >
                <FontAwesomeIcon icon={faEllipsis} />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={handleToggleReadStatus}>
                  {isRead ? 'Marcar como no leído' : 'Marcar como leído'}
                </Dropdown.Item>
                <Dropdown.Item href="#!">Mark Important</Dropdown.Item>
                <Dropdown.Item href="#!">Archive</Dropdown.Item>
                <Dropdown.Item href="#!" className="text-danger">
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <span
            className={classNames('fs-10', {
              'fw-normal text-body-tertiary': isRead,
              'fw-bold text-body-emphasis': !isRead
            })}
          >
            {email.time}
          </span>
        </Col>
      </Row>
      
      <div className="ms-4 mt-n3 mt-sm-0 ms-sm-11">
        <div className="d-block">
          <span
            className={classNames('fs-9 line-clamp-1', {
              'text-body-highlight fw-normal': isRead,
              'text-body-emphasis fw-bold': !isRead
            })}
            title={cleanedSubject}
          >
            {!isRead && (
              <span className="badge badge-primary badge-sm me-2">NUEVO</span>
            )}
            {cleanedSubject}
          </span>
          <p 
            className={classNames('fs-9 ps-0 mb-0 line-clamp-2', {
              'text-body-tertiary': isRead,
              'text-body fw-medium': !isRead
            })}
            title={cleanedDetails}
          >
            {cleanedDetails}
          </p>
        </div>
      </div>
      
      {loading && (
        <div 
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10
          }}
        >
          <Spinner animation="border" size="sm" />
        </div>
      )}
    </div>
  );
};

export default EmailRow;